import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Separator } from '../../ui/separator';
import { CampaignReportData } from '../../../types/campaignReport';
import { formatPercent, formatNumberCompact, formatCurrencyGBP } from '../../../lib/reportFormatters';
import { ArrowRight } from 'lucide-react';

interface FunnelTabProps {
  data: CampaignReportData;
}

export function FunnelTab({ data }: FunnelTabProps) {
  const footfall = data.kpis.footfall.sixMonthTotal;
  const browsing = data.kpis.browsingSessions.sixMonthTotal;
  const purchases = data.kpis.totalUnitsSold;
  
  const footfallToBrowseRate = data.kpis.footfallToBrowseRate || 
    (browsing / footfall * 100);
  const browseToPurchaseRate = data.conversions.browseToPurchaseRate;
  const footfallToPurchaseRate = data.conversions.footfallToPurchaseRate;

  return (
    <div className="space-y-8">
      {/* Conversion Performance Table */}
      <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
        <CardHeader>
          <CardTitle className="text-xl font-bold" style={{ color: '#183df7' }}>
            3️⃣ Conversion Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">Metric</TableHead>
                <TableHead className="text-right font-semibold">Result</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">Conversion: Footfall → Purchase</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatPercent(footfallToPurchaseRate)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Conversion: Browsing → Purchase</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatPercent(browseToPurchaseRate)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Average Selling Price</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatCurrencyGBP(data.conversions.averageSellingPrice)}
                </TableCell>
              </TableRow>
              {data.conversions.monthlyUnitsSold && (
                <TableRow>
                  <TableCell className="font-medium">Monthly Units Sold</TableCell>
                  <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                    {formatNumberCompact(data.conversions.monthlyUnitsSold)}
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell className="font-medium">Total Units (6 months)</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatNumberCompact(purchases)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Visual Funnel Steps */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="text-center" style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-muted-foreground mb-2">Footfall (Zone)</p>
            <p className="text-3xl font-bold mb-1" style={{ color: '#183df7' }}>
              {formatNumberCompact(footfall)}
            </p>
            <p className="text-xs text-muted-foreground">6-month total</p>
          </CardContent>
        </Card>

        <div className="flex flex-col items-center justify-center">
          <ArrowRight className="w-8 h-8 mb-2" style={{ color: '#183df7' }} />
          <p className="text-sm font-semibold" style={{ color: '#183df7' }}>
            {formatPercent(footfallToBrowseRate)}
          </p>
          <p className="text-xs text-muted-foreground">Conversion</p>
        </div>

        <Card className="text-center" style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-muted-foreground mb-2">Browsing Sessions</p>
            <p className="text-3xl font-bold mb-1" style={{ color: '#183df7' }}>
              {formatNumberCompact(browsing)}
            </p>
            <p className="text-xs text-muted-foreground">6-month total</p>
          </CardContent>
        </Card>

        <div className="md:col-span-3 flex justify-center my-4">
          <ArrowRight className="w-8 h-8 rotate-90 md:rotate-0" style={{ color: '#183df7' }} />
        </div>

        <div className="md:col-span-3">
          <Card className="text-center" style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardContent className="pt-6">
              <p className="text-sm font-medium text-muted-foreground mb-2">Purchases (Units)</p>
              <p className="text-3xl font-bold mb-1" style={{ color: '#183df7' }}>
                {formatNumberCompact(purchases)}
              </p>
              <p className="text-xs text-muted-foreground mb-3">Total units sold</p>
              <Separator className="my-3" />
              <p className="text-sm text-muted-foreground">Browse → Purchase:</p>
              <p className="text-lg font-semibold" style={{ color: '#183df7' }}>
                {formatPercent(browseToPurchaseRate)}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
